/** Matches backend default: 10 minutes for email / SMS / WhatsApp. */
export const OTP_RESEND_COOLDOWN_MS = 10 * 60 * 1000;

export function getOtpResendStorageKey({ purpose = 'login', type, identifier }) {
  return `resendTime_${purpose}_${type}_${identifier || ''}`;
}

export function getOtpResendTimeLeftMs(storageKey) {
  const until = parseInt(localStorage.getItem(storageKey), 10);
  if (!Number.isFinite(until)) return 0;
  const left = until - Date.now();
  if (left <= 0) {
    localStorage.removeItem(storageKey);
    return 0;
  }
  return left;
}

export function markOtpResendCooldown(storageKey, durationMs = OTP_RESEND_COOLDOWN_MS) {
  const ms = Math.max(0, Number(durationMs) || OTP_RESEND_COOLDOWN_MS);
  const until = Date.now() + ms;
  localStorage.setItem(storageKey, String(until));
  return until;
}

export function getVerifyPathForOtp(type, identifier) {
  if (type === 'email') {
    return `/auth/verify/email/${encodeURIComponent(identifier)}`;
  }
  return `/auth/verify/${type}/${identifier}`;
}

export function getRetryAfterSeconds(error) {
  const body = error?.response?.data;
  const value = body?.data?.retryAfterSeconds ?? body?.retryAfterSeconds;
  if (Number.isFinite(Number(value)) && Number(value) > 0) {
    return Math.ceil(Number(value));
  }
  return null;
}

export function isOtpCooldownError(error) {
  const msg = error?.response?.data?.msg || error?.response?.data?.message || error?.message || '';
  // Max verify attempts also uses HTTP 429 — that is not a resend cooldown.
  if (/maximum attempts/i.test(String(msg))) return false;
  if (error?.response?.data?.data?.otpStillActive) return true;
  if (getRetryAfterSeconds(error) != null) return true;
  if (error?.response?.status === 429) return true;
  return /please wait|before requesting|otpStillActive/i.test(String(msg));
}

/**
 * After a login OTP attempt: open verify with an accurate remaining timer.
 * - Existing client cooldown → navigate without calling API again (caller checks first).
 * - Fresh send → mark full cooldown.
 * - Server 429 → mark remaining seconds and navigate (otp still active).
 */
function navigateToOtpVerify(navigate, {
  type,
  identifier,
  authFlow = 'login',
  retryAfterSeconds,
  otpStillActive = false,
}) {
  const storageKey = getOtpResendStorageKey({ purpose: authFlow, type, identifier });
  if (Number.isFinite(retryAfterSeconds) && retryAfterSeconds > 0) {
    markOtpResendCooldown(storageKey, retryAfterSeconds * 1000);
  }

  navigate(getVerifyPathForOtp(type, identifier), {
    state: {
      otpAlreadySent: true,
      authFlow,
      otpStillActive: Boolean(otpStillActive),
      retryAfterSeconds: otpStillActive && retryAfterSeconds > 0 ? retryAfterSeconds : undefined,
    },
  });
}

/**
 * Login OTP: ask the server. A local timer is not proof a code is still stored.
 */
export async function requestLoginOtpAndNavigate({ type, identifier, navigate, sendFn }) {
  const storageKey = getOtpResendStorageKey({ purpose: 'login', type, identifier });

  try {
    const response = await sendFn();
    const cooldownSec = response?.data?.cooldownSeconds;
    const durationMs =
      Number.isFinite(Number(cooldownSec)) && Number(cooldownSec) > 0
        ? Number(cooldownSec) * 1000
        : OTP_RESEND_COOLDOWN_MS;
    markOtpResendCooldown(storageKey, durationMs);
    navigateToOtpVerify(navigate, {
      type,
      identifier,
      authFlow: 'login',
      otpStillActive: false,
    });
    return { reused: false, data: response };
  } catch (error) {
    if (isOtpCooldownError(error)) {
      const retryAfter =
        getRetryAfterSeconds(error) ?? Math.ceil(OTP_RESEND_COOLDOWN_MS / 1000);
      navigateToOtpVerify(navigate, {
        type,
        identifier,
        authFlow: 'login',
        retryAfterSeconds: retryAfter,
        otpStillActive: true,
      });
      return { reused: true, rateLimited: true };
    }
    throw error;
  }
}
